/**
 * Error Categorization Utilities
 *
 * Functions to categorize and determine severity of errors
 *
 * @module lib/errors/errorCategories
 */

import { AppError, ErrorCategory, ErrorSeverity } from './AppError';

/**
 * Categorize an unknown error into an ErrorCategory
 */
export function categorizeError(error: unknown): ErrorCategory {
  // If it's already an AppError, return its category
  if (error instanceof AppError) {
    return error.category;
  }

  // If it's a standard Error, try to categorize by message
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Network errors
    if (
      message.includes('network') ||
      message.includes('fetch failed') ||
      message.includes('connection') ||
      message.includes('net::err')
    ) {
      return ErrorCategory.NETWORK_ERROR;
    }

    // Timeout errors
    if (message.includes('timeout') || message.includes('timed out')) {
      return ErrorCategory.TIMEOUT_ERROR;
    }

    // Auth errors
    if (
      message.includes('unauthorized') ||
      message.includes('authentication') ||
      message.includes('invalid credentials')
    ) {
      return ErrorCategory.AUTH_ERROR;
    }

    // Permission errors
    if (
      message.includes('forbidden') ||
      message.includes('permission') ||
      message.includes('access denied')
    ) {
      return ErrorCategory.PERMISSION_ERROR;
    }

    // Session errors
    if (
      message.includes('session') ||
      message.includes('token expired') ||
      message.includes('invalid token')
    ) {
      return ErrorCategory.SESSION_ERROR;
    }

    // Validation errors
    if (message.includes('validation') || message.includes('invalid')) {
      return ErrorCategory.VALIDATION_ERROR;
    }

    // Camera errors
    if (
      message.includes('camera') ||
      message.includes('notfoundederror') ||
      message.includes('notallowederror')
    ) {
      return ErrorCategory.CAMERA_ERROR;
    }

    // Parse errors
    if (message.includes('parse') || message.includes('json') || message.includes('syntax')) {
      return ErrorCategory.PARSE_ERROR;
    }
  }

  // Default to unknown
  return ErrorCategory.UNKNOWN_ERROR;
}

/**
 * Determine the severity of an error based on its category and context
 */
function determineSeverity(category: ErrorCategory, statusCode?: number): ErrorSeverity {
  // Status code-based severity (for API errors)
  if (statusCode) {
    if (statusCode >= 500) {
      return ErrorSeverity.CRITICAL;
    }
    if (statusCode === 401 || statusCode === 403) {
      return ErrorSeverity.ERROR;
    }
    if (statusCode === 404 || statusCode === 400) {
      return ErrorSeverity.WARNING;
    }
  }

  // Category-based severity
  switch (category) {
    case ErrorCategory.NETWORK_ERROR:
    case ErrorCategory.API_ERROR:
      return ErrorSeverity.ERROR;

    case ErrorCategory.TIMEOUT_ERROR:
      return ErrorSeverity.WARNING;

    case ErrorCategory.AUTH_ERROR:
    case ErrorCategory.PERMISSION_ERROR:
    case ErrorCategory.SESSION_ERROR:
      return ErrorSeverity.ERROR;

    case ErrorCategory.VALIDATION_ERROR:
      return ErrorSeverity.WARNING;

    case ErrorCategory.RENDER_ERROR:
    case ErrorCategory.STATE_ERROR:
      return ErrorSeverity.CRITICAL;

    case ErrorCategory.CONFIG_ERROR:
      return ErrorSeverity.CRITICAL;

    case ErrorCategory.CAMERA_ERROR:
    case ErrorCategory.DEVICE_ERROR:
      return ErrorSeverity.ERROR;

    case ErrorCategory.DATA_ERROR:
    case ErrorCategory.PARSE_ERROR:
      return ErrorSeverity.ERROR;

    case ErrorCategory.UNKNOWN_ERROR:
    default:
      return ErrorSeverity.ERROR;
  }
}

/**
 * Convert an unknown error to an AppError
 */
export function toAppError(error: unknown): AppError {
  // Already an AppError
  if (error instanceof AppError) {
    return error;
  }

  // Standard Error
  if (error instanceof Error) {
    const category = categorizeError(error);
    const severity = determineSeverity(category);

    return new AppError(error.message, category, severity, {
      cause: error,
      details: {
        originalName: error.name,
        originalStack: error.stack,
      },
    });
  }

  // String
  if (typeof error === 'string') {
    const category = categorizeError(new Error(error));
    const severity = determineSeverity(category);

    return new AppError(error, category, severity);
  }

  // Unknown type
  return new AppError(
    'An unknown error occurred',
    ErrorCategory.UNKNOWN_ERROR,
    ErrorSeverity.ERROR,
    {
      details: {
        originalError: error,
      },
    }
  );
}
